import { useMemo } from "react";
import { useModels } from "@/hooks/useModels";

interface DbMessage {
  role: string;
  metadata?: {
    partial?: boolean;
    usage?: { input_tokens?: number; total_tokens?: number };
    model?: string;
  } | null;
}

export type ContextUsageLevel = "ok" | "warn" | "danger";

export interface ConversationContextUsage {
  /** Tokens reportados pelo OpenRouter no último turno de assistant. */
  lastInputTokens: number;
  /** Janela de contexto do modelo atualmente selecionado. */
  contextWindow: number;
  /** Razão lastInputTokens / contextWindow (0–1+). */
  ratio: number;
  /** ok < 0.6, warn 0.6–0.85, danger > 0.85. */
  level: ContextUsageLevel;
  /** Nome do modelo usado na medição (pode diferir do atual). */
  measuredModelLabel: string | null;
  /** True se o modelo selecionado mudou desde o último turno medido. */
  modelChanged: boolean;
  /** True se há medição real disponível. Quando false, não renderizar. */
  hasMeasurement: boolean;
}

/**
 * Lê metadata.usage.input_tokens da última mensagem assistant da conversa
 * (medido pelo OpenRouter) e compara com a janela de contexto do modelo
 * atualmente selecionado. Não faz estimativa local — se não houver
 * medição, retorna `hasMeasurement: false` e o caller não renderiza nada.
 */
export function useConversationContextUsage(
  dbMessages: DbMessage[] | undefined,
  currentModelId: string | undefined,
): ConversationContextUsage | null {
  const { data: models } = useModels();

  return useMemo(() => {
    if (!dbMessages || dbMessages.length === 0 || !currentModelId) return null;

    // Override para testes manuais no devtools:
    //   window.__caldeiraContextRatio = 0.9   // força danger
    //   window.__caldeiraContextRatio = 0.7   // força warn
    //   delete window.__caldeiraContextRatio  // volta ao normal
    // Requer que a conversa já tenha ao menos uma resposta do modelo (para haver
    // measurement real) — o override só substitui a razão, mantendo o resto.
    const override =
      typeof window !== "undefined"
        ? Number((window as any).__caldeiraContextRatio)
        : NaN;


    // Última mensagem assistant não-parcial com usage.input_tokens.
    let lastInputTokens = 0;
    let measuredModelId: string | null = null;
    for (let i = dbMessages.length - 1; i >= 0; i--) {
      const m = dbMessages[i];
      if (m.role !== "assistant") continue;
      if (m.metadata?.partial === true) continue;
      const tokens = Number(m.metadata?.usage?.input_tokens ?? 0);
      if (tokens > 0) {
        lastInputTokens = tokens;
        measuredModelId = m.metadata?.model ?? null;
        break;
      }
    }
    if (lastInputTokens <= 0) return null;

    const currentModel = models?.find((m) => m.id === currentModelId);
    const contextWindow = currentModel?.context_window ?? 0;
    if (!contextWindow) return null;

    const ratio = lastInputTokens / contextWindow;
    const measuredModel = measuredModelId ? models?.find((m) => m.id === measuredModelId) : null;

    const effectiveRatio = Number.isFinite(override) && override > 0 ? override : ratio;
    const effectiveLevel: ContextUsageLevel =
      effectiveRatio > 0.85 ? "danger" : effectiveRatio > 0.6 ? "warn" : "ok";

    return {
      lastInputTokens:
        Number.isFinite(override) && override > 0
          ? Math.round(effectiveRatio * contextWindow)
          : lastInputTokens,
      contextWindow,
      ratio: effectiveRatio,
      level: effectiveLevel,
      measuredModelLabel: measuredModel?.display_name ?? measuredModelId ?? null,
      modelChanged: !!measuredModelId && measuredModelId !== currentModelId,
      hasMeasurement: true,
    };

  }, [dbMessages, currentModelId, models]);
}

